import { useState } from 'react';
import { Mail } from 'lucide-react';
import { Alert, Button, Input, Modal, Select, Textarea } from '@tutor/ui';
import { ApiError } from '../../lib/api-client';
import { useCurriculum, useInviteStudent } from '../../hooks/queries';

export interface InviteStudentDialogProps {
  open: boolean;
  onClose: () => void;
}

export function InviteStudentDialog({ open, onClose }: InviteStudentDialogProps) {
  const { data: curriculum } = useCurriculum();
  const invite = useInviteStudent();

  const [email, setEmail] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const reset = (): void => {
    setEmail('');
    setSubjectId('');
    setMessage('');
    setError(null);
    setSentTo(null);
  };

  const close = (): void => {
    reset();
    onClose();
  };

  const onSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    try {
      const invitation = await invite.mutateAsync({
        email: email.trim(),
        subjectId: subjectId || undefined,
        message: message.trim() || undefined,
      });
      setSentTo(invitation.email);
      setEmail('');
      setMessage('');
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (err.fieldError('email') ?? err.message)
          : 'We could not send that invitation.',
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Invite a student"
      description="They will appear on your roster once they accept."
      footer={
        <>
          <Button variant="outline" onClick={close}>
            {sentTo ? 'Done' : 'Cancel'}
          </Button>
          <Button type="submit" form="invite-student-form" isLoading={invite.isPending}>
            Send invitation
          </Button>
        </>
      }
    >
      <form id="invite-student-form" onSubmit={onSubmit} className="space-y-4" noValidate>
        {sentTo && (
          <Alert tone="success" title="Invitation sent">
            {sentTo} will see it the next time they sign in. You can send another below.
          </Alert>
        )}

        {error && <Alert tone="danger">{error}</Alert>}

        <Input
          label="Student's email address"
          type="email"
          required
          autoComplete="off"
          placeholder="student@example.com"
          leftIcon={<Mail className="h-4 w-4" aria-hidden />}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          hint="They do not need an account yet — the invitation waits for them."
        />

        <Select
          label="Subject"
          hint="Optional. Scopes the relationship to one subject."
          value={subjectId}
          onChange={(event) => setSubjectId(event.target.value)}
        >
          <option value="">Any subject</option>
          {curriculum?.map((subject) => (
            <option key={subject.id} value={subject.id}>
              {subject.name}
            </option>
          ))}
        </Select>

        <Textarea
          label="Message"
          hint="Optional."
          rows={3}
          maxLength={500}
          placeholder="Looking forward to working with you."
          value={message}
          onChange={(event) => setMessage(event.target.value)}
        />
      </form>
    </Modal>
  );
}
